import useAppStore from "@/context/store";
import { report } from "@/services";
import { gdSlsReportError } from "@/utils/gdSlsRum";
import useRequest from "@/utils/useReq";
import classNames from "classnames";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { getMessage, getUnreadNum } from "../../services/notify";
import getTimeDuration from "../../utils/getTimeDuration";
import imageUrl from "../../utils/imageUrl";
import HBAvatar from "../Avatar";
import HBIcon from "../HBIcon";
import HBList from "../HBList";
import HBMessage from "../HBMessage";
import HBTabButton from "../HBTabButton";
import Image from "../Image";
import ReportModal from "../ReportModal";
import styles from "./index.module.less";

const classifyOptions = [
  { name: "消息", value: "mentions" },
  { name: "动态", value: "activities" },
];

export default function Notify({
  activeTab,
  height = 300,
  limit = 20,
  avatarSize = 32,
  wrapId = "message-wrap",
  tabStyle,
  itemStyle = {},
  wrapStyle = {},
  maxLength = 34,
  onChange,
}) {
  const [state, setState] = useState(activeTab || "mentions");
  const unreadNum = useAppStore((s) => s.unreadNum);

  // 设置未读的红点样式
  const [optionClass, setOptionClass] = useState({
    mentions: null,
    activities: null,
  });

  useEffect(() => {
    setState(activeTab || "mentions");
  }, [activeTab]);

  async function onLoad(messages) {
    const unreads = messages.filter((msg) => msg.unread).length;
    if (unreads) {
      // 获取当前未读数量，如果有就代表另一边有未读消息
      const result = await getUnreadNum();
      if (result > 0) {
        setOptionClass({
          mentions: styles.tabUnread,
          activities: styles.tabUnread,
        });
      } else {
        // 设置另一边没有红点
        setOptionClass({
          ...{
            mentions: "",
            activities: "",
          },
          [state]: styles.tabUnread,
        });
      }
      // 更新未读状态
      useAppStore.setState({
        unreadNum: result,
      });
    } else {
      if (unreadNum) {
        // 设置另一边为未读样式
        setOptionClass({
          ...optionClass,
          [state === "mentions" ? "activities" : "mentions"]: styles.tabUnread,
        });
      } else {
        setOptionClass({
          mentions: "",
          activities: "",
        });
      }
    }
  }

  return (
    <div>
      <HBTabButton
        options={classifyOptions}
        value={state}
        onChange={(option, prevValue) => {
          setState(option.value);
          setOptionClass({ ...optionClass, [prevValue]: null });
          onChange && onChange(option.value, prevValue);
        }}
        optionClass={optionClass}
        style={tabStyle}
      />
      <MessageList
        key={state}
        wrapId={wrapId}
        type={state}
        onLoad={onLoad}
        height={height}
        limit={limit}
        avatarSize={avatarSize}
        itemStyle={itemStyle}
        wrapStyle={wrapStyle}
        maxLength={maxLength}
      />
    </div>
  );
}

export function MessageList({
  wrapId,
  type,
  onLoad,
  height = 300,
  limit = 20,
  avatarSize = 32,
  itemStyle = {},
  wrapStyle = {},
  maxLength = 40,
}) {
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const { data } = useRequest(
    async () => {
      return getMessage({
        type,
        per_page: limit,
        page,
      }).then((res) => {
        if (res.messages.length < limit) {
          setHasMore(false);
        }

        if (page === 1) onLoad && onLoad(res.messages);

        if (!data) return res;

        return {
          messages: [...data.messages, ...res.messages],
        };
      });
    },
    {
      refreshDeps: [page],
    }
  );

  const onLoadMore = useCallback(() => {
    setPage(page + 1);
  }, [page]);

  const renderItem = (msg) => {
    return (
      <MessageItem
        message={msg}
        key={wrapId + msg.message_id}
        avatarSize={avatarSize}
        itemStyle={itemStyle}
        maxLength={maxLength}
      />
    );
  };

  return (
    <div
      id={wrapId}
      className={styles.wrap}
      style={{
        padding: "12px 0 0 0",
        height: height,
        overflow: "auto",
        ...wrapStyle,
      }}
    >
      <If condition={data?.messages?.length}>
        <Then>
          <HBList
            hasMore={hasMore}
            disableMasonry={true}
            render={renderItem}
            dataSource={data?.messages}
            onLoadMore={onLoadMore}
            scrollableTarget={wrapId}
            endMessage={null}
            listStyle={{
              display: "block",
            }}
          />
        </Then>
        <Else>
          <div className={styles.empty}>还没有动态消息哦~</div>
        </Else>
      </If>
    </div>
  );
}

function MessageItem({ message, avatarSize, itemStyle, maxLength }) {
  const { type, extra, created_at, unread } = message;
  let { pin, comment, by_pin } = extra;
  const currentUser = useAppStore((s) => s.user);

  const { textArray, byUser } = useMemo(() => {
    try {
      const { type, extra } = message;
      let { by_user: byUser, board, pin, comment, through_pin } = extra;
      let textArray = [
        {
          // 兼容没有by_user 脏数据的情况
          text: byUser?.username || "未知用户",
          url: byUser ? "/user/" + byUser.urlname : "",
        },
      ];

      switch (type) {
        case "follow_user":
          // {用户名}关注了你
          textArray.push({
            text: "关注了你",
          });
          break;
        case "follow_board":
          // {用户名}关注了你的画板{画板标题}
          textArray.push({
            text: "关注了你的画板",
          });
          textArray.push({
            url: "/boards/" + board.board_id,
            text: board.title,
          });
          break;
        case "repin":
          // {用户名}转采了你的采集{采集描述}
          textArray.push({
            text: "转采了你的采集",
          });
          textArray.push({
            url: "/pins/" + pin.pin_id,
            text: pin.raw_text,
          });
          break;
        case "like_pin":
          // {用户名}喜欢了你的采集{采集描述}
          textArray.push({
            text: "喜欢了你的采集",
          });
          textArray.push({
            url: "/pins/" + pin.pin_id,
            text: pin.raw_text,
          });
          break;
        case "like_board":
          // {用户名}喜欢了你的画板{画板标题}
          textArray.push({
            text: "喜欢了你的画板",
          });
          textArray.push({
            url: "/boards/" + board.board_id,
            text: board.title,
          });
          break;
        case "repin_2nd":
          // {用户名}通过{用户名}转采了{采集描述}
          textArray.push({
            text: "通过",
          });
          textArray.push({
            text: through_pin.user.username,
            url: "/user/" + through_pin.user.urlname,
          });
          textArray.push({
            text: "转采了",
          });
          textArray.push({
            url: "/pins/" + pin.pin_id,
            text: pin.raw_text,
          });
          break;
        case "comment":
          // {用户名}评论了采集{采集描述}
          textArray.push({
            text: "评论了采集",
          });
          textArray.push({
            url: "/pins/" + pin.pin_id,
            text: comment.raw_text,
          });
          break;
        case "pin_text":
          // {用户名}在采集{采集名称}时提到了你
          textArray.push({
            text: "在采集",
          });
          textArray.push({
            url: "/pins/" + pin.pin_id,
            text: pin.raw_text,
          });
          textArray.push({
            text: "时提到了你",
          });
          break;
        case "board_member_join":
          // {用户名} 已加入多人画板 {画板名称} ，快来一起采集灵感吧！
          textArray.push({
            text: "已加入多人画板",
          });
          textArray.push({
            url: `/boards/${board.board_id}`,
            text: board.title,
          });
          textArray.push({
            text: "，快来一起采集灵感吧！",
          });
          break;
        case "board_member_leave":
          // {用户名} 已退出多人画板 {画板名称}
          textArray.push({
            text: "已退出多人画板",
          });
          textArray.push({
            url: `/boards/${board.board_id}`,
            text: board.title,
          });
          break;
        case "board_member_remove":
          textArray = [];
          textArray.push({
            text: "你已被移出多人画板",
          });
          textArray.push({
            url: `/boards/${board.board_id}`,
            text: board.title,
          });
          textArray.push({
            text: "，你的采集可在「我的灵感空间-采集」中查看及编辑。",
          });
          break;
        case "collab_board_remove":
          // 多人画板 {画板名称} 已被删除，相关采集也将一并移除。如有疑问请联系画板创建者。
          textArray = [];
          textArray.push({
            text: "多人画板",
          });
          textArray.push({
            url: `/boards/${extra?.board_id}`,
            text: board?.title || extra?.board_id,
          });
          textArray.push({
            text: "已被删除，相关采集也将一并移除。如有疑问请联系画板创建者。",
          });
          break;
        case "collab_board_recover":
          // 多人画板 {画板名称} 已被恢复，相关采集也将一并恢复。如有疑问请联系画板创建者。
          textArray = [];
          textArray.push({
            text: "多人画板",
          });
          textArray.push({
            url: `/boards/${extra?.board_id}`,
            text: board?.title || extra?.board_id,
          });
          textArray.push({
            text: "已被恢复，相关采集也将一并恢复。如有疑问请联系画板创建者。",
          });
          break;
      }
      return {
        textArray,
        byUser,
      };
    } catch (error) {
      gdSlsReportError(error, {
        message: "动态消息解析错误",
        content: JSON.stringify(message),
      });
      return {
        textArray: null,
        byUser: null,
      };
    }
  }, [message, currentUser]);

  if (!textArray) return null;

  return (
    <div
      className={classNames({ [styles.unread]: unread }, styles.item)}
      style={itemStyle}
    >
      {byUser && <Avatar user={byUser} size={avatarSize} />}
      <Info
        textArray={textArray}
        createdTime={created_at}
        maxLength={maxLength}
      />
      <When condition={type === "comment" || type === "repin"}>
        <HBIcon
          type="ic_alert"
          data-gd-click="button_click"
          data-button-name="举报"
          data-report-type={type === "comment" ? "私信" : "采集"}
          {...(type === "comment"
            ? { "data-report-user-id": comment?.user_id }
            : {
                "data-report-pin-id": by_pin?.pin_id,
                "data-report-content-id": by_pin?.file_material?.material_id,
                "data-report-file-id": by_pin?.file_id,
              })}
          onClick={() => {
            ReportModal.open({
              reportType: type === "comment" ? "私信" : "采集",
              ...(type === "comment"
                ? { reportUserId: comment?.user_id }
                : {
                    reportPinId: by_pin?.pin_id,
                    reportContentId: by_pin?.file_material?.material_id,
                    reportFileId: by_pin?.file_id,
                  }),
              onOk(values) {
                report({
                  type: type,
                  ...values,
                  id: comment?.comment_id || by_pin?.pin_id,
                }).then((res) => {
                  if (res.err) {
                    if (res.err === "already reported") {
                      HBMessage.info("你已经举报过了，我们会及时处理哦，谢谢!");
                    } else {
                      HBMessage.err(res.msg || res.err);
                    }
                  }

                  HBMessage.success("举报成功");
                });
              },
            });
          }}
        ></HBIcon>
      </When>
      {(by_pin || pin) && (
        <Link href={`/pins/${(by_pin || pin).pin_id}`}>
          <a>
            <Image
              className={styles.pinview}
              src={imageUrl((by_pin || pin).file, { width: 55 })}
              alt={(by_pin || pin).raw_text}
              width={55}
              height={55}
            />
          </a>
        </Link>
      )}
    </div>
  );
}

function Avatar({ user, size = 32 }) {
  return (
    <Link href={"/user/" + user.urlname}>
      <a className={classNames(styles.avatar)}>
        <HBAvatar user={user} size={size} />
      </a>
    </Link>
  );
}
// 是否省略
function isOmit(el) {
  return el.url || el.isOmit;
}

function Info({ textArray, createdTime, maxLength }) {
  const allText = textArray.reduce((a, b) => a + b.text, "");

  if (maxLength !== "nolimit" && maxLength < allText.length) {
    // 中间词文本总长度
    const actionTextLength = textArray
      .filter((el) => !isOmit(el))
      .reduce((a, b) => a + b.text, "").length;
    // 除去文本词长度
    const last = maxLength - actionTextLength;

    let omitTextNum = textArray.filter(isOmit).length;
    let limitLength = last / omitTextNum;

    textArray = textArray.map((el) => {
      if (isOmit(el)) {
        omitTextNum--;
        if (el.text.length < limitLength) {
          limitLength += (limitLength - el.text.length) / omitTextNum;
          return {
            ...el,
          };
        }
        return {
          ...el,
          raw_text: el.text,
          text: maxString(el.text, limitLength),
        };
      }
      return {
        ...el,
      };
    });
  }

  function renderInfo(el, index) {
    if (el.url) {
      return (
        <Link key={index} href={el.url}>
          <a>{el.text}</a>
        </Link>
      );
    }
    return <span key={index}>{el.text}</span>;
  }

  return (
    <div className={styles.info}>
      <div className="text">{textArray.map(renderInfo)}</div>
      <div className={styles.time}>
        {getTimeDuration({ time: createdTime * 1000 })}
      </div>
    </div>
  );
}

/**
 * 截取前length长度的字符串，后面并拼接。。。
 * @param {string} str
 * @param {number} length
 * @returns {string}
 */
function maxString(str, length = 10) {
  if (str.length <= length + 3) return str;
  return str.substring(0, length) + "...";
}
